import { createContext, type ElementType, useContext } from 'react'
import { Typography } from '@healingpaper/cell'
import { useRouter } from 'next/router'
import { IcArrowRight } from '@healingpaper/cell-icons'
import { useTranslation } from 'react-i18next'
import { getEnv } from '@unni/platform'
import Link from 'next/link'

import { DistrictFilterTargetEntityNames } from '../district-filter.types'

import { postAmplitude } from '@/core/lib'
import { useLocales } from '@/shared/hooks/useLocales'

import type { DistrictCity, DistrictsResponse } from '../api/get-districts'

// Context 생성
const DistrictContext = createContext<{
  targetEntityName: DistrictFilterTargetEntityNames
  cityList: DistrictCity[]
}>({ targetEntityName: DistrictFilterTargetEntityNames.EVENTS, cityList: [] })

// 메인 컴포넌트
export function DistrictFilter({
  children,
  district,
  targetEntityName,
}: {
  children: React.ReactNode
  district: DistrictsResponse
  targetEntityName: DistrictFilterTargetEntityNames
}) {
  const cityList = district.cityList || []

  return (
    <DistrictContext.Provider value={{ targetEntityName, cityList }}>
      <div className="flex flex-col flex-1 h-full w-full">{children}</div>
    </DistrictContext.Provider>
  )
}

// 서브 컴포넌트
function Header() {
  const { t } = useTranslation()
  return (
    <header className="flex items-center justify-center h-[108px] border-b border-gray-200">
      <Typography type="title2" color="gray900">
        {t('pages.districtFilter.title')}
      </Typography>
    </header>
  )
}

function CityList() {
  const { cityList } = useContext(DistrictContext)
  const router = useRouter()
  const hash = router.asPath.split('#')[1]
  const activeCityCode = hash ? hash : ''

  return (
    <aside className="flex flex-col sticky top-0 overflow-y-auto min-w-[120px] border-r border-gray-200 hide-scrollbar">
      {cityList.map((city) => (
        <a
          key={city.code}
          className={`border-l-[3px] pl-5 py-3 ${activeCityCode === city.code ? 'border-[#FF540F]' : 'border-white'}`}
          href={`#${city.code}`}
          onClick={(e) => {
            e.preventDefault()
            const element = document.getElementById(city.name)
            if (element) {
              element.scrollIntoView({ behavior: 'smooth' })
            }
            router.push(`#${city.code}`)
          }}
        >
          <Typography type="title3" color={activeCityCode === city.code ? 'normalPrimary' : 'gray800'}>
            {city.name}
          </Typography>
        </a>
      ))}
    </aside>
  )
}

function DistrictList() {
  const { countryPathnamePrefix } = useLocales()
  const { cityList, targetEntityName } = useContext(DistrictContext)
  const router = useRouter()
  const prefixPath = countryPathnamePrefix

  return (
    <main className="flex flex-col flex-1 min-w-0">
      {cityList.map((city) => {
        const hasOnlyOneSubDistrictWithSameName =
          city.neighborhoodVicinityList.length === 1 && city.neighborhoodVicinityList[0].name === city.name
        const url = getDistrictUrl(
          router.query.q as string,
          hasOnlyOneSubDistrictWithSameName ? city.neighborhoodVicinityList[0].id : city.code,
          prefixPath,
          targetEntityName,
        )
        return (
          <section key={city.code}>
            <DistrictAnchor
              id={city.name}
              href={url.toString()}
              onClick={(e) => {
                postAmplitude('click_district_button', {
                  district_code: city.code,
                  district_name: city.name,
                  target_url: url.toString(),
                })
              }}
            >
              {city.name}
            </DistrictAnchor>
            {!hasOnlyOneSubDistrictWithSameName && (
              <ul>
                {city.neighborhoodVicinityList.map((district) => {
                  const url = getDistrictUrl(router.query.q as string, district.id, prefixPath, targetEntityName)
                  return (
                    <li key={district.id}>
                      <DistrictAnchor
                        href={url.toString()}
                        className="pl-6"
                        onClick={(e) => {
                          postAmplitude('click_district_button', {
                            district_code: district.id,
                            district_name: district.name,
                            target_url: url.toString(),
                          })
                        }}
                      >
                        {district.name}
                      </DistrictAnchor>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>
        )
      })}
      <div className="min-h-[90%] w-full" />
    </main>
  )
}

const getDistrictUrl = (
  q: string,
  district: string,
  prefixPath: string,
  targetEntityName: DistrictFilterTargetEntityNames,
) => {
  const params = new URLSearchParams()
  if (q) {
    params.set('q', q)
  }
  params.set('district', district)

  const queryString = params.toString()
  return `${prefixPath}/${targetEntityName}${queryString ? `?${queryString}` : ''}`
}

interface DistrictAnchorProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  children: React.ReactNode
  className?: string
  elementType?: ElementType
  href: string
}

function DistrictAnchor({ children, href, className, elementType = 'span', ...htmlDefaultProps }: DistrictAnchorProps) {
  return (
    <Link
      className={`h-12 w-full px-4 py-3 flex items-center justify-between border-b border-gray-200 min-w-0 ${className || ''}`}
      href={href}
      {...htmlDefaultProps}
    >
      <Typography
        elementType={elementType}
        type={'description1'}
        color="gray900"
        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {children}
      </Typography>
      <div className="flex items-center justify-center min-w-[24px] min-h-[24px]">
        <IcArrowRight color="#666666" width={24} height={24} />
      </div>
    </Link>
  )
}

DistrictFilter.Header = Header
DistrictFilter.CityList = CityList
DistrictFilter.DistrictList = DistrictList
